// Structured rendering of a BSE StAR MF 2.0 response for the API workbench (and any
// screen that shows a raw exchange reply). The backend (_bse_passthrough in
// routes_bse2.py) attaches a `rendered` summary — outcome + English messages, built
// from bse_v2.errors/envelope, the single source of truth for BSE's message
// catalogue. This file never re-implements that classification; it only turns
// `rendered` and the raw `data` into readable layout, and degrades to something
// sensible (never blank, never a crash) on any response shape it hasn't seen before.
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { openBseAction } from "@/components/bse/BseUatForm";
import { AlertTriangle, CheckCircle2, ExternalLink, XCircle } from "lucide-react";
import { chooseColumns, extractBody, humanizeKey, humanizeNested, isPlainObject, rowKey, rowsFromData } from "@/lib/bseResponse";

const OUTCOME_META = {
  accepted: { label: "Accepted by BSE", tone: "border-emerald-200 bg-emerald-50 text-emerald-900", Icon: CheckCircle2 },
  rejected: { label: "Rejected by BSE", tone: "border-rose-200 bg-rose-50 text-rose-900", Icon: XCircle },
  ambiguous: { label: "Outcome unclear", tone: "border-amber-200 bg-amber-50 text-amber-900", Icon: AlertTriangle },
};

function EmptyNote({ children }) {
  return <p className="rounded-lg border border-dashed border-[var(--glass-border)] p-3 text-xs text-[var(--ink-muted)]">{children}</p>;
}

function renderScalar(value) {
  if (value === null || value === undefined || value === "") return <span className="text-[var(--ink-muted)]">—</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

// Collapsed-by-default expander for an object/array-valued cell. The nested
// content itself is built by humanizeNested() — a plain, non-JSX recursive
// function (see lib/bseResponse.js for why: this project's dev build always
// runs a babel plugin that cannot handle a JSX component rendering itself).
// Arbitrarily deep BSE payloads (history[], nomination{}, ...) still expand
// on demand; they just never dump open as one long flat wall of JSON.
function NestedValue({ value }) {
  const count = Array.isArray(value) ? value.length : Object.keys(value || {}).length;
  if (count === 0) return <span className="text-[var(--ink-muted)]">—</span>;
  return (
    <details>
      <summary className="cursor-pointer text-xs text-[var(--cta)]">{count} {Array.isArray(value) ? "item" : "field"}{count === 1 ? "" : "s"}</summary>
      <pre className="mt-1 whitespace-pre-wrap rounded-md border border-[var(--glass-border)] bg-[var(--surface-2)] p-2 text-xs">{humanizeNested(value)}</pre>
    </details>
  );
}

// Cell/value renderer shared by the list table and the key/value detail view.
// Small flat objects (e.g. {ucc: "..."}) render inline rather than behind an
// expander — that is the common "one useful field nested one level down" case
// (investor.ucc, bank_acct.ifsc) and inlining it is what keeps nesting from
// reading as unreadable soup.
function renderCellValue(value) {
  if (value === null || value === undefined || value === "") return <span className="text-[var(--ink-muted)]">—</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[var(--ink-muted)]">—</span>;
    if (value.every((item) => item === null || typeof item !== "object")) {
      return value.map((item) => (item === null || item === "" ? "—" : String(item))).join(", ");
    }
    return <NestedValue value={value} />;
  }
  const keys = Object.keys(value);
  if (keys.length === 0) return <span className="text-[var(--ink-muted)]">—</span>;
  if (keys.length <= 2 && keys.every((key) => value[key] === null || typeof value[key] !== "object")) {
    return keys.map((key) => `${humanizeKey(key)}: ${value[key] === null || value[key] === "" ? "—" : String(value[key])}`).join(", ");
  }
  return <NestedValue value={value} />;
}

function DataTable({ rows, totalCount }) {
  if (rows.every((row) => !isPlainObject(row))) {
    return <ul className="list-inside list-disc space-y-1 text-sm">{rows.map((row, index) => <li key={`${String(row)}-${index}`}>{renderScalar(row)}</li>)}</ul>;
  }
  const columns = chooseColumns(rows);
  const totalFieldCount = new Set(rows.flatMap((row) => (isPlainObject(row) ? Object.keys(row) : []))).size;
  const hiddenFieldCount = Math.max(0, totalFieldCount - columns.length);
  return (
    <div className="space-y-1.5">
      {typeof totalCount === "number" && totalCount !== rows.length && (
        <p className="text-xs text-[var(--ink-muted)]">Showing {rows.length} of {totalCount} total rows.</p>
      )}
      <div className="overflow-x-auto rounded-xl border border-[var(--glass-border)]">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((key) => (
                <TableHead key={key} className="whitespace-nowrap">
                  <div>{humanizeKey(key)}</div>
                  <div className="font-mono text-[10px] font-normal text-[var(--ink-muted)]">{key}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={rowKey(row)}>
                {columns.map((key) => (
                  <TableCell key={key} className="align-top text-xs">{isPlainObject(row) ? renderCellValue(row[key]) : "—"}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {hiddenFieldCount > 0 && (
        <p className="text-[11px] text-[var(--ink-muted)]">{hiddenFieldCount} additional field{hiddenFieldCount === 1 ? "" : "s"} per row not shown here — see the full response JSON below.</p>
      )}
    </div>
  );
}

function DetailView({ data }) {
  const entries = Object.entries(data);
  if (entries.length === 0) return <EmptyNote>BSE returned an empty object for this call.</EmptyNote>;
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--glass-border)]">
      <table className="w-full text-sm">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-[var(--glass-border)] align-top last:border-0">
              <td className="w-1/3 min-w-[140px] bg-[var(--surface-2)] p-2.5">
                <div className="text-xs font-medium">{humanizeKey(key)}</div>
                <div className="font-mono text-[10px] text-[var(--ink-muted)]">{key}</div>
              </td>
              <td className="p-2.5 text-sm">{renderCellValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DataView({ data, hasBody }) {
  if (!hasBody) return <EmptyNote>No response body was available to inspect — the call may not have reached BSE at all.</EmptyNote>;
  if (data === null || data === undefined) {
    return <EmptyNote>BSE returned no data for this call. That is a legitimate outcome, not necessarily an error — check the messages above for why.</EmptyNote>;
  }
  const rows = rowsFromData(data);
  if (rows) {
    if (rows.length === 0) return <EmptyNote>BSE returned zero rows.</EmptyNote>;
    const totalCount = isPlainObject(data) && typeof data.total_count === "number" ? data.total_count : undefined;
    return <DataTable rows={rows} totalCount={totalCount} />;
  }
  if (isPlainObject(data)) return <DetailView data={data} />;
  return <p className="font-mono text-sm">{renderScalar(data)}</p>;
}

function MessageList({ messages }) {
  if (!messages.length) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
        {messages.length === 1 ? "Message from BSE" : `Messages from BSE (${messages.length})`}
      </h4>
      <ul className="space-y-2">
        {messages.map((message) => {
          const key = [message.code, message.errcode, message.field, Array.isArray(message.vals) ? message.vals.join("|") : ""]
            .filter((part) => part !== undefined && part !== null && part !== "").join(":") || message.text || "message";
          return (
            <li key={key} className="rounded-lg border border-[var(--glass-border)] bg-white p-3">
              <p className="text-sm font-medium">{message.text}</p>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--ink-muted)]">
                {message.field && <span>Field <code className="rounded bg-slate-100 px-1">{message.field}</code></span>}
                {Array.isArray(message.vals) && message.vals.length > 0 && <span>Value <code className="rounded bg-slate-100 px-1">{message.vals.join(", ")}</code></span>}
                {message.code != null && <span className="opacity-70">code {message.code}</span>}
                {message.errcode && <span className="opacity-70">{message.errcode}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Action-link extraction (2FA / payment links) — unchanged from the previous
// implementation, kept exactly so nothing here regresses.
function findActionLinks(response) {
  const links = [];
  const actions = [];
  const visit = (item) => {
    if (!item || typeof item !== "object") return;
    if (typeof item.href === "string" && /^(https?:\/\/|upi:)/.test(item.href)) actions.push(item);
    Object.entries(item).forEach(([key, child]) => {
      if (typeof child === "string" && /^(https?:\/\/|upi:)/.test(child) && /(url|link|href|2fa)/i.test(key)) links.push(child);
      else if (child && typeof child === "object") visit(child);
    });
  };
  visit(response);
  return { links, actions };
}

export function Result({ value }) {
  if (!value) return <div className="rounded-xl border border-dashed p-8 text-center text-sm text-[var(--ink-muted)]">The real BSE response will appear here.</div>;
  const response = value.response || value;
  const { links, actions } = findActionLinks(response);
  const actionLinks = new Set(actions.map((action) => action.href));

  const callFailed = ["error", "call_failed"].includes(value.status);
  const rendered = isPlainObject(value.rendered) ? value.rendered : null;
  const outcome = rendered?.outcome && OUTCOME_META[rendered.outcome] ? rendered.outcome : "ambiguous";
  const outcomeReason = rendered?.outcome_reason || (rendered ? "" : "This response predates structured outcome rendering — inspect the raw JSON below.");
  const messages = Array.isArray(rendered?.messages) ? rendered.messages : [];
  const meta = OUTCOME_META[outcome];

  const body = extractBody(response);
  const data = body ? body.data : undefined;

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 text-sm ${meta.tone}`}>
        <div className="flex flex-wrap items-center gap-2">
          <meta.Icon className="h-4 w-4" />
          <span className="font-semibold">{meta.label}</span>
          <Badge variant="outline" className="bg-white/70 text-[10px] uppercase tracking-wide">{callFailed ? "call failed" : "response received"}</Badge>
        </div>
        {outcomeReason && <p className="mt-1.5 text-xs leading-relaxed">{outcomeReason}</p>}
        {outcome === "accepted" && (
          <p className="mt-1.5 text-xs leading-relaxed opacity-90">
            Receiving HTTP 200 is not exchange acceptance by itself — this outcome is derived from BSE&apos;s status and messages together, not the HTTP result alone.
          </p>
        )}
      </div>

      {(actions.length > 0 || links.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action, index) => (
            <Button key={`${action.href}-${index}`} type="button" size="sm" className="rounded-full bg-[var(--cta)]" onClick={() => openBseAction(action)}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open BSE {action.rel || "action"}
            </Button>
          ))}
          {[...new Set(links)].filter((link) => !actionLinks.has(link)).map((link) => (
            <Button key={link} type="button" size="sm" className="rounded-full bg-[var(--cta)]" onClick={() => openBseAction(link)}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open BSE action link
            </Button>
          ))}
        </div>
      )}

      <MessageList messages={messages} />

      <div className="space-y-1.5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Data returned</h4>
        <DataView data={data} hasBody={!!body} />
      </div>

      <details className="rounded-xl border bg-slate-950 text-emerald-200">
        <summary className="cursor-pointer px-4 py-3 text-xs font-semibold">Full exchange response (raw JSON)</summary>
        <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap border-t border-slate-800 p-4 text-xs">{JSON.stringify(response, null, 2)}</pre>
      </details>
    </div>
  );
}
